import { useMemo, useState } from "react";
import { chapters, volumes } from "@/mocks/manga";

const PAGE_SIZE = 10;

export default function Catalog() {
  const [vol, setVol] = useState("全部");
  const [kw, setKw] = useState("");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const k = kw.trim();
    return chapters.filter((c) => {
      const matchVol = vol === "全部" || c.volume === vol;
      const matchKw = !k || c.title.includes(k) || String(c.num).includes(k);
      return matchVol && matchKw;
    });
  }, [vol, kw]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const filters = ["全部", ...volumes];

  const resetPage = (fn: () => void) => {
    fn();
    setPage(1);
  };

  return (
    <section id="catalog" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
              <i className="ri-list-check-2 text-sm" />
              全部章节
            </span>
            <h2 className="font-heading mt-3 text-3xl text-foreground-950 md:text-4xl">
              泡在我家的辣妹漫画全集目录
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              共 {chapters.length} 话，支持按卷筛选与标题搜索，点开即可直达最新内容。
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex flex-wrap gap-1 rounded-md bg-background-100 p-1">
              {filters.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => resetPage(() => setVol(f))}
                  className={`whitespace-nowrap rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                    vol === f
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:bg-background-200/70"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 rounded-md bg-background-100 px-3 py-2">
              <i className="ri-search-line text-sm text-foreground-500" />
              <input
                type="text"
                value={kw}
                onChange={(e) => resetPage(() => setKw(e.target.value))}
                placeholder="搜索话数或标题"
                className="w-40 bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
              />
            </label>
          </div>
        </div>

        <div className="mt-8 overflow-hidden rounded-lg ring-1 ring-background-200/70">
          <div className="hidden grid-cols-12 gap-4 bg-background-100 px-5 py-3 text-xs font-medium text-foreground-600 md:grid">
            <div className="col-span-2">章节</div>
            <div className="col-span-5">标题</div>
            <div className="col-span-2">所属卷</div>
            <div className="col-span-2">页数</div>
            <div className="col-span-1 text-right">操作</div>
          </div>
          <ul>
            {paged.map((c) => (
              <li
                key={c.id}
                className="grid grid-cols-1 gap-2 border-t border-background-200/70 px-5 py-3.5 transition-colors hover:bg-background-100/50 md:grid-cols-12 md:items-center md:gap-4"
              >
                <div className="flex items-center gap-2 md:col-span-2">
                  <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded bg-primary-100 text-xs font-bold text-primary-800">
                    {c.num}
                  </span>
                  <span className="text-xs text-foreground-600 md:hidden">第{c.num}话</span>
                </div>
                <div className="text-sm font-medium text-foreground-900 md:col-span-5">
                  《{c.title}》
                  <span className="ml-2 text-xs text-foreground-500 md:hidden">{c.volume}</span>
                </div>
                <div className="hidden text-sm text-foreground-600 md:col-span-2 md:block">
                  {c.volume}
                </div>
                <div className="hidden text-sm text-foreground-600 md:col-span-2 md:block">
                  {c.pages} 页 · {c.date}
                </div>
                <div className="md:col-span-1 md:text-right">
                  <a
                    href="#faq"
                    className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-secondary-100 px-2.5 py-1.5 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    阅读
                    <i className="ri-arrow-right-s-line" />
                  </a>
                </div>
              </li>
            ))}
          </ul>
          {filtered.length === 0 && (
            <div className="px-5 py-14 text-center text-sm text-foreground-500">
              没有找到匹配「{kw}」的章节，换个关键词试试。
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              disabled={safePage === 1}
              onClick={() => setPage(safePage - 1)}
              className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-foreground-700 transition-colors hover:bg-background-200/70 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="上一页"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                className={`flex h-9 w-9 items-center justify-center rounded-md text-sm font-medium transition-colors ${
                  p === safePage
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200/70"
                }`}
              >
                {p}
              </button>
            ))}
            <button
              type="button"
              disabled={safePage === totalPages}
              onClick={() => setPage(safePage + 1)}
              className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-foreground-700 transition-colors hover:bg-background-200/70 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="下一页"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}